/**
 * costProjection.js — Phase 5 Intelligence Layer.
 *
 * Projects remaining project cost to completion.
 *
 * Math:
 *   hoursRemaining = etaWeeks × totalTeamHoursPerWeek
 *   cost = hoursRemaining × avgWeightedRate
 *
 *   - etaWeeks has variance (from etaProjection)
 *   - totalTeamHoursPerWeek is near-deterministic (known from velocities)
 *   - avgWeightedRate is deterministic (known per-annotator rates)
 *
 * So cost variance propagates linearly from ETA variance. The band is:
 *   cost_low  = eta_low  × team_hours_per_week × weighted_rate
 *   cost_point= eta_point × ...
 *   cost_high = eta_high × ...
 *
 * Edge cases:
 *   - ETA insufficient → cost insufficient
 *   - Zero team hours → insufficient (no cost can be computed)
 *   - Weighted rate is 0 → deterministic(0) (no cost, valid state)
 *
 * Pure function. Money output is NUMERIC-compatible (rounded to 2 decimals).
 */

import { truncate } from '../statistics';
import { deterministic, insufficientData, fromStatsBand, validateProjection } from './confidenceBand';

/**
 * Compute team hours per week from velocities. Sum of per-annotator
 * hoursPerWeek.
 */
function totalHoursPerWeek(velocities) {
  if (!Array.isArray(velocities)) return 0;
  return velocities.reduce((acc, v) => {
    const h = typeof v.hoursPerWeek === 'number' && Number.isFinite(v.hoursPerWeek) ? v.hoursPerWeek : 0;
    return acc + h;
  }, 0);
}

/**
 * Compute cost-weighted average rate (weighted by hoursPerWeek).
 * This matches what actual spending will be, accounting for the fact
 * that higher-hour annotators contribute more to cost.
 */
function weightedAvgRate(velocities) {
  if (!Array.isArray(velocities) || velocities.length === 0) return 0;
  let totalHours = 0;
  let totalSpend = 0;
  for (const v of velocities) {
    const h = typeof v.hoursPerWeek === 'number' && Number.isFinite(v.hoursPerWeek) ? v.hoursPerWeek : 0;
    const r = typeof v.rate === 'number' && Number.isFinite(v.rate) ? v.rate : 0;
    totalHours += h;
    totalSpend += h * r;
  }
  if (totalHours === 0) return 0;
  return totalSpend / totalHours;
}

/**
 * Round money to 2 decimals (matching NUMERIC(12,2)).
 *
 * R2 LOW-6 audit fix: ordering convention.
 *   We round BEFORE clamping (truncate) below. This means extreme edge
 *   values like -0.0049 would round to -0.00 → clamp → 0. A "clamp-then-
 *   round" order would do the same, so the choice is semantically
 *   equivalent on the zero boundary — but "round first, clamp second"
 *   guarantees the final output has at most 2 decimals (no residual
 *   float error from clamp's Math.max/min). The M5 round-trip test
 *   locks this contract.
 */
function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * @param {Projection} etaProjection — from projectETA
 * @param {Array} velocities — annotator velocities with {hoursPerWeek, rate}
 * @param {object} [_config] — unused today; reserved for future rate-inflation scenarios
 * @param {Date} [now] — R2 LOW-5: inject time for test stability / React identity
 * @returns {Projection} cost in USD
 * @throws if etaProjection is present but malformed (R2 HIGH-2 audit fix)
 */
export function projectCost(etaProjection, velocities, _config, now = new Date()) {
  // R2 HIGH-2 audit fix: validate input contract. Previously a malformed
  // ETA (e.g., band-ordering violation {point:5, low:7, high:8} from a
  // hand-rolled caller) passed straight through — projectCost would emit
  // `{point: 4000, low: 5600, high: 6400}` which FAILS validateProjection
  // downstream, corrupting the Projection registry. Guard at the boundary.
  if (etaProjection != null) {
    validateProjection(etaProjection, 'projectCost: etaProjection');
  }
  if (!etaProjection || etaProjection.confidence === 'insufficient_data') {
    return insufficientData('USD', 'no ETA projection', now);
  }
  if (etaProjection.point == null) {
    return insufficientData('USD', 'ETA has no point estimate', now);
  }

  const teamHoursPerWeek = totalHoursPerWeek(velocities);
  const rate = weightedAvgRate(velocities);

  if (teamHoursPerWeek === 0) {
    return insufficientData('USD', 'no team hours per week', now);
  }
  if (rate === 0) {
    // Team is working but no rates set → cost is $0 (legitimate state,
    // not insufficient data). Deterministic.
    return deterministic(0, 'USD', now);
  }

  const coefficient = teamHoursPerWeek * rate;

  const band = {
    point: round2(etaProjection.point * coefficient),
    low:   etaProjection.low  == null ? null : round2(etaProjection.low  * coefficient),
    high:  etaProjection.high == null ? null : round2(etaProjection.high * coefficient),
    method: etaProjection.method,
    sampleSize: etaProjection.sampleSize,
  };

  // Non-negative clamp — can't have negative cost.
  const truncated = truncate(band, 0);

  // R2 MED-1 audit fix: if the band collapsed to a point after truncation
  // (e.g., eta was deterministic, or eta.low was clamped hard enough that
  // all three bounds meet), the projection is semantically 'deterministic',
  // not 'gaussian'. Downgrade method so downstream UX doesn't render a
  // phantom variance band around a known-exact value.
  const allEqual = truncated.point != null
    && truncated.low != null
    && truncated.high != null
    && truncated.point === truncated.low
    && truncated.point === truncated.high;
  const effectiveMethod = allEqual ? 'deterministic' : truncated.method;
  // R3 L5 audit fix: when method collapses to 'deterministic' (band
  // width = 0), sampleSize should be 1 to match the deterministic()
  // helper's convention. Carrying the ETA's n (e.g. 5) through a
  // deterministic method is semantically inconsistent.
  const effectiveSampleSize = allEqual ? 1 : truncated.sampleSize;
  return fromStatsBand(
    { ...truncated, method: effectiveMethod, sampleSize: effectiveSampleSize },
    etaProjection.confidence,
    'USD',
    now,
  );
}
